import React, { forwardRef, useEffect, useState } from 'react'
import { useCombobox, UseComboboxPropGetters } from 'downshift'

import { TextInput, TextInputProps } from '../TextInput'
import { InputGroup } from '../InputGroup'
import { InputRightElement } from '../InputElement'
import { Icon } from '../Icon'
import { Box, BoxProps } from '../Box'
import { Text } from '../Text'
import { Spinner } from '../Spinner'
import { Portal } from '../Portal'
import { useDimensions } from '../../hooks/useDimensions'
import { usePrevious } from '../../hooks/usePrevious'
import { theme } from '../../theme'
import { useDebounce } from 'use-debounce'
import axios from 'axios'
const AddressInputMenuWrapper = (props: BoxProps) => (
  <Box
    position="fixed"
    shadow="lg"
    maxWidth="500px"
    zIndex={theme.zIndices.dropdown}
    bg="white"
    border="2px"
    borderColor="gray.300"
    mt={1}
    {...props}
  />
)

/////////////////////////////////////////////////////////////////////////////////////////////////

interface AddressInputMenu extends BoxProps {
  isOpen: boolean
  searchResults: SearchResult[]
  highlightedIndex: number
  getMenuProps: UseComboboxPropGetters<any>['getMenuProps']
  getItemProps: UseComboboxPropGetters<any>['getItemProps']
  isLoading: boolean
  searchValue: string
}

const AddressInputMenu = ({
  isOpen,
  searchResults,
  minWidth,
  top,
  left,
  highlightedIndex,
  getMenuProps,
  getItemProps,
  isLoading,
  searchValue,
}: AddressInputMenu) => (
  <Portal>
    <Box as="ul" {...getMenuProps()} zIndex={99999}>
      {isOpen &&
        (searchResults.length > 0 ? (
          <AddressInputMenuWrapper minWidth={minWidth} top={top} left={left}>
            {searchResults.map((item, index) => (
              <Box
                as="li"
                bg={highlightedIndex === index ? 'green.500' : null}
                key={`${item?.id}`}
                px={3}
                py={2}
                cursor="pointer"
                listStyleType="none"
                borderBottom="1px"
                borderColor="gray.200"
                {...getItemProps({ item, index })}
              >
                <Text color={highlightedIndex === index ? 'white' : 'black'} fontWeight="medium">
                  {item?.description}
                </Text>
              </Box>
            ))}
          </AddressInputMenuWrapper>
        ) : isLoading ? null : searchValue.length === 0 ? null : (
          <AddressInputMenuWrapper minWidth={minWidth} top={top} left={left} py={4} px={3}>
            <Text>Sorry, no results!</Text>
          </AddressInputMenuWrapper>
        ))}
    </Box>
  </Portal>
)

/////////////////////////////////////////////////////////////////////////////////////////////////

export type AddressObject = {
  address_line1: string
  address_line2: string
  postcode: string
  city: string
  state: string
  lat: number
  lng: number
}

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T

export interface SearchResult {
  description: string
  locality: string
  lat: number
  long: number
  id: number
  postcode: string
  state: string
}

export interface AddressInputProps extends Omit<TextInputProps, 'onChangeValue'> {
  defaultValue?: any // TODO: determine what the default value should be of this input
  onChangeValue?: (value: AddressObject | string) => void
  onChangeInputValue?: (value: string) => void
  doesReturnStringOnChangeValue?: boolean
  transformResult?: (result: AddressObject) => any
}

export const AddressInput = forwardRef(
  (
    {
      defaultValue,
      onChangeValue,
      onChangeInputValue,
      doesReturnStringOnChangeValue,
      transformResult,
      size,
      onFocus,
      onBlur,
      ...restProps
    }: AddressInputProps,
    ref,
  ) => {
    const [searchValue, setSearchValue] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [searchResults, setSearchResults] = useState<SearchResult[]>([])

    const [debouncedSearch] = useDebounce(searchValue, 300)

    useEffect(() => {
      setIsLoading(true)

      const fetchSearchResults = async () => {
        const results = await axios.get(
          `https://postcodes-bzwn2mw5ya-km.a.run.app/search/${debouncedSearch}?client=tc`,
        )
        setSearchResults(
          results.data
            .reduce((unique, entry) => {
              const key = `${entry.locality}-${entry.state}`
              return unique.some(e => `${e.locality}-${e.state}` === key)
                ? unique
                : [...unique, entry]
            }, [])
            .filter(entry => entry.type)
            .map(entry => ({
              ...entry,
              description: `${entry.locality}, ${entry.state}`,
            })),
        )
        setIsLoading(false)
      }

      fetchSearchResults()
    }, [debouncedSearch])

    const previousDefaultValue = usePrevious(defaultValue)

    useEffect(() => {
      if (defaultValue && defaultValue != previousDefaultValue) {
        setSearchValue(defaultValue)
      }
    }, [defaultValue])

    const [anchorRef, dimensions] = useDimensions()

    const comboBox = useCombobox<SearchResult>({
      items: searchResults,
      initialInputValue: restProps.value as string,
      // selectedItem: defaultValue,
      onSelectedItemChange: async ({ selectedItem }) => {
        const addressString = selectedItem.description

        const processedDetails: AddressObject = {
          address_line1: null,
          address_line2: null,
          city: selectedItem.locality,
          lat: selectedItem.lat,
          lng: selectedItem.long,
          postcode: selectedItem.postcode,
          state: selectedItem.state,
        }

        const value = doesReturnStringOnChangeValue
          ? addressString
          : transformResult?.(processedDetails) || processedDetails

        onChangeValue && onChangeValue(value)
      },
      itemToString: item => (item ? item.description : ''),
      onInputValueChange: ({ inputValue }) => {
        const onlyDigitsRegex = new RegExp('^[0-9]*$')

        /* Check to see if the input text is numbers only (ie: Postcode),
         * Only make a google api request if the result is false (ie: anything other than a postcode ) */
        if (!onlyDigitsRegex.test(inputValue)) {
          setSearchValue(inputValue.trim())
        }

        onChangeInputValue && onChangeInputValue(inputValue.trim())
      },
      onIsOpenChange: ({ isOpen }) => {
        // Sometimes when the menu opens, it is nowhere near the input. This fixes it
        isOpen && window.dispatchEvent(new Event('resize'))
      },
    })

    return (
      <Box ref={anchorRef}>
        <InputGroup size={size} {...comboBox.getComboboxProps()}>
          <TextInput
            placeholder={'e.g. Melbourne'}
            {...restProps}
            {...comboBox.getInputProps({ onFocus, onBlur, ref: ref as any })}
            autoComplete="off"
          />

          <InputRightElement>
            {isLoading ? (
              <Spinner size="sm" color="green.500" />
            ) : (
              <Icon name="place" color="gray.400" fontSize="1.2rem" />
            )}
          </InputRightElement>
        </InputGroup>

        <AddressInputMenu
          isOpen={comboBox.isOpen && searchResults.length > 0}
          searchResults={searchResults}
          searchValue={searchValue}
          isLoading={isLoading}
          minWidth={`${dimensions.width}px`}
          top={`${dimensions.y + dimensions.height}px`}
          left={`${dimensions.x}px`}
          getMenuProps={comboBox.getMenuProps}
          getItemProps={comboBox.getItemProps}
          highlightedIndex={comboBox.highlightedIndex}
        />
      </Box>
    )
  },
)

export default AddressInput
